/**
 * The inbox: notification records, what needs the person first, then what is for them, then
 * the rest. An approval or a decision is acted on in place; anything else is marked read or
 * done. One list serves the project page (its own records) and the Inbox view (every project).
 */

import { shortRunId } from './ui/RunId.tsx';
import { attentionFor, RISK_LEVEL_WORDS, hasManyTeams, OPEN_MY_TASKS_ACTION, OPEN_VIEW_ACTION, oneRecordPerTask, SUBSCRIPTION_SIGN_IN_ACTION, TEAMWORK_ANSWER_RPC, WORKFLOW_APPROVAL_DECIDE_RPC, WORKFLOW_ERRORS, type InboxItem, type Notification } from '@aioe/shared';
import { ArrowDownToLine, At, Bell, CircleCheck, CircleExclamation, CircleInfo, CircleQuestion, Comment, CreditCard, Hand, NodesRight, Pencil, ShieldCheck } from '@gravity-ui/icons';
import { ActionBar } from '@heroui-pro/react';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { SignInAnswer, SignInLink, signInWaitsFor } from './signIn.tsx';
import { SubjectName } from './SubjectPicker.tsx';
import { Button, Empty, ErrorNote, ListBlock, ListGroup, StatusMark, Tick } from './ui.tsx';
import { RowMenu } from './ui/RowMenu.tsx';
import { VIEW_TONES } from './ui/Art.tsx';
import { useAgentName } from '../agentNames.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { Markdown } from './Markdown.tsx';
import { answersApproval, approvalDecision, liveHref, liveOfNotification } from './live/liveModel.ts';
import { liveCall, liveProblem } from './live/useLiveSession.ts';

/** The platform's action on a catalogue submission (packages/api routes/catalogue.ts). */
const CATALOGUE_DECIDE_RPC = 'aioe.catalogue.submission.decide';
/** Approve or Decline on someone's ask for an exception (packages/api policyExceptions/service.ts, D379). */
const EXCEPTION_DECIDE_RPC = 'aioe.policy.exception.decide';

const ATTENTION_ORDER: Record<Notification['attention'], number> = { 'needs-you': 0, 'for-you': 1, fyi: 2 };
const ATTENTION_TEXT: Record<Notification['attention'], string> = { 'needs-you': 'Needs you', 'for-you': 'For you', fyi: 'FYI' };
const KIND_TEXT: Record<Notification['kind'], string> = {
  approval: 'Approval',
  decision: 'Decision',
  question: 'Question',
  done: 'Done',
  failed: 'Failed',
  changed: 'Changed',
  mention: 'Mention',
  update: 'Update',
  policy: 'Policy',
  budget: 'Budget',
  node: 'Node',
  info: 'Info',
};

/** One icon per kind, the workbench's (webview-ui shell/notificationKindIcons.tsx). */
const KIND_ICON: Record<Notification['kind'], ReactNode> = {
  approval: <Hand />,
  decision: <CircleQuestion />,
  question: <Comment />,
  done: <CircleCheck />,
  failed: <CircleExclamation />,
  changed: <Pencil />,
  mention: <At />,
  update: <ArrowDownToLine />,
  policy: <ShieldCheck />,
  budget: <CreditCard />,
  node: <NodesRight />,
  info: <CircleInfo />,
};

/**
 * Open before settled, then by attention as this person sees it ("For you" only when addressed to them), then unread
 * before read, then newest. `newest` is newest first only (the Unread view: exactly what is unread, in time order).
 */
export function sortInbox<T extends Notification>(items: T[], me?: string, order: 'attention' | 'newest' = 'attention'): T[] {
  if (order === 'newest') return [...items].sort((a, b) => b.at.localeCompare(a.at));
  const live = (n: Notification) => (n.state === 'unread' || n.state === 'read' ? 0 : 1);
  const rank = (n: Notification) => ATTENTION_ORDER[attentionFor(n, { id: me })];
  const unread = (n: Notification) => (n.state === 'unread' ? 0 : 1);
  return [...items].sort((a, b) => live(a) - live(b) || rank(a) - rank(b) || unread(a) - unread(b) || b.at.localeCompare(a.at));
}

export { oneRecordPerTask };

const keyOf = (n: Pick<Notification, 'projectId' | 'id'>) => `${n.projectId}/${n.id}`;
const settledState = (n: Notification) => n.state === 'done' || n.state === 'expired';

/** Marks these records read or done in one call; what was skipped (gone, changed meanwhile) is simply refreshed away. */
export async function markMany(items: Notification[], state: 'read' | 'done'): Promise<number> {
  const picked = items.filter((n) => !settledState(n) && (state === 'done' || n.state === 'unread'));
  if (!picked.length) return 0;
  let changed = 0;
  for (let i = 0; i < picked.length; i += 200) {
    const out = await routes.setNotificationStates({ state, items: picked.slice(i, i + 200).map((n) => ({ projectId: n.projectId, id: n.id, ifVersion: n.version })) });
    changed += out.changed;
  }
  return changed;
}

/**
 * "Mark all as read": every unread record the person may see (in one project, or across all of them), not only the
 * page shown, so the count and the bell go to zero. Shown while the list has, or the count says there is, something unread.
 */
export function MarkAllRead({ items, unread, projectId, onChange }: { items: Notification[]; unread?: number; projectId?: string; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  if (!items.some((n) => n.state === 'unread') && !unread) return null;
  return (
    <Button
      size="sm"
      variant="ghost"
      isPending={busy}
      onPress={() => {
        setBusy(true);
        void routes
          .setNotificationStates({ state: 'read', all: true, ...(projectId ? { projectId } : {}) })
          .then(onChange)
          .finally(() => setBusy(false));
      }}
    >
      Mark all as read
    </Button>
  );
}

/**
 * The records as one list group: the heading (title, count, a muted line, its actions) outside, one card of rows
 * inside, an empty state in place of the card when there is nothing to show; the bar for a selection floats under it.
 */
export function InboxList({
  items,
  onChange,
  showProject,
  emptyTitle,
  emptyBody,
  selectable,
  order,
  title,
  description,
  actions,
  error: loadError,
}: {
  items: InboxItem[];
  onChange: () => void;
  showProject?: boolean;
  emptyTitle?: string;
  emptyBody?: string;
  selectable?: boolean;
  order?: 'attention' | 'newest';
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** Why the list could not be read: shown inside the card. */
  error?: string;
}) {
  const tone = VIEW_TONES.inbox!;
  const { account } = useAuth();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<'read' | 'done'>();
  const [error, setError] = useState<string>();
  if (items.length === 0)
    return (
      <ListGroup
        title={title}
        description={description}
        actions={actions}
        empty={loadError ? undefined : <Empty tone={tone} icon={<Bell />} title={emptyTitle ?? 'Nothing waiting'} body={emptyBody ?? 'Approvals, decisions and changes to your projects show here, and on your phone.'} compact />}
      >
        <ListBlock>
          <ErrorNote error={loadError} />
        </ListBlock>
      </ListGroup>
    );
  // A team is named only on a project that has more than one (D239).
  const manyTeams = new Set([...new Set(items.map((n) => n.projectId))].filter((pid) => hasManyTeams(items.filter((n) => n.projectId === pid).map((n) => n.teamId ?? n.teamName))));
  const sorted = sortInbox(items, account?.id, order);
  // Only records still open can be selected; a selection outlives a refresh only for records still shown.
  const selectableItems = sorted.filter((n) => !settledState(n));
  const chosen = selectableItems.filter((n) => selected.has(keyOf(n)));
  const toggle = (n: Notification, on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(keyOf(n));
      else next.delete(keyOf(n));
      return next;
    });
  const allChosen = selectableItems.length > 0 && chosen.length === selectableItems.length;
  const markChosen = async (state: 'read' | 'done') => {
    setBusy(state);
    setError(undefined);
    try {
      await markMany(chosen, state);
      setSelected(new Set());
      onChange();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <>
      <ListGroup title={title} count={items.length} description={description} actions={actions}>
        {selectable && selectableItems.length > 1 && (
          <div className="flex items-center gap-3 text-xs text-muted">
            <Tick checked={allChosen} onChange={(on) => setSelected(on ? new Set(selectableItems.map(keyOf)) : new Set())} label="Select every open record shown">
              {allChosen ? 'All selected' : 'Select all'}
            </Tick>
            {chosen.length > 0 && <span className="tabular-nums">{chosen.length} selected</span>}
          </div>
        )}
        {sorted.map((n) => (
          <InboxRow key={keyOf(n)} n={n} onChange={onChange} showProject={showProject} showTeam={manyTeams.has(n.projectId)} {...(selectable && !settledState(n) ? { selected: selected.has(keyOf(n)), onSelect: (on: boolean) => toggle(n, on) } : selectable ? { gutter: true } : {})} />
        ))}
        {error || loadError ? (
          <ListBlock>
            <ErrorNote error={error ?? loadError} />
          </ListBlock>
        ) : null}
      </ListGroup>
      {selectable && (
        <div className="pointer-events-none sticky bottom-6 z-40 flex justify-center">
          <ActionBar isOpen={chosen.length > 0} aria-label="Selected records" className="!relative !inset-auto">
            <ActionBar.Prefix>
              <span className="px-2 text-sm tabular-nums">{chosen.length === 1 ? '1 record' : `${chosen.length} records`}</span>
            </ActionBar.Prefix>
            <ActionBar.Suffix>
              <Button size="sm" variant="ghost" isDisabled={Boolean(busy)} onPress={() => setSelected(new Set())}>
                Clear
              </Button>
              {chosen.some((n) => n.state === 'unread') && (
                <Button size="sm" variant="secondary" isPending={busy === 'read'} isDisabled={Boolean(busy)} onPress={() => void markChosen('read')}>
                  Mark read
                </Button>
              )}
              <Button size="sm" variant="primary" isPending={busy === 'done'} isDisabled={Boolean(busy)} onPress={() => void markChosen('done')}>
                Done
              </Button>
            </ActionBar.Suffix>
          </ActionBar>
        </div>
      )}
    </>
  );
}

function InboxRow({ n, onChange, showProject, showTeam, selected, onSelect, gutter }: { gutter?: boolean; n: InboxItem; onChange: () => void; showProject?: boolean; showTeam?: boolean; selected?: boolean; onSelect?: (on: boolean) => void }) {
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [answering, setAnswering] = useState<string>();
  const [answer, setAnswer] = useState('');
  const navigate = useNavigate();
  const settled = n.state === 'done' || n.state === 'expired';
  // A record whose only action opens something (a mention) is still read and done by hand.
  const onlyOpens = n.actions.length > 0 && n.actions.every((a) => a.rpc === OPEN_VIEW_ACTION);
  // A Team Work question answered in words (no option to pick): the answer goes as the person, through the record.
  // A workflow's Reject or Hold (D401) asks why first: the reason goes with the action, and the run records it.
  const asksWhy = (a: Notification['actions'][number] | undefined) => a?.rpc === WORKFLOW_APPROVAL_DECIDE_RPC && (a.params as { decision?: string } | undefined)?.decision !== 'approved';
  const answeringAction = n.actions.find((a) => a.id === answering);
  const sendAnswer = async (id: string) => {
    if (!answer.trim()) return;
    setBusy(id);
    setError(undefined);
    try {
      if (asksWhy(n.actions.find((a) => a.id === id))) await routes.actNotification(n.projectId, n.id, id, answer.trim());
      else await routes.answerNotification(n.projectId, n.id, id, answer.trim());
      setAnswer('');
      setAnswering(undefined);
      onChange();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  const act = async (id: string) => {
    setBusy(id);
    setError(undefined);
    try {
      await routes.actNotification(n.projectId, n.id, id);
      onChange();
    } catch (e) {
      // A high-impact workflow approval asks for a recent sign-in: said in the contract's words.
      setError((e as { code?: string }).code === 'sign_in_again' ? WORKFLOW_ERRORS.sign_in_again : (e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  const mark = async (state: Notification['state']) => {
    setBusy(state);
    setError(undefined);
    try {
      await routes.setNotificationState(n.projectId, n.id, n.version, state);
      onChange();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  // D282: an approval on a session under remote control is answered over the relay, as the person, from the web.
  const live = liveOfNotification(n);
  const openLive = live ? liveHref(live.live.deviceId, live.live) : undefined;
  const answerLive = async (id: string, decision: 'allow' | 'deny', params?: Record<string, unknown>) => {
    if (!live) return;
    setBusy(id);
    setError(undefined);
    try {
      const p = params ?? {};
      const sessionId = (typeof p.sessionId === 'string' && p.sessionId) || live.sessionId || n.sessionId;
      const permissionId = (typeof p.permissionId === 'string' && p.permissionId) || live.permissionId;
      await liveCall(live.live.deviceId, 'permissions.resolve', {
        ...p,
        ...(sessionId ? { sessionId } : {}),
        ...(live.live.conversationId ? { conversationId: live.live.conversationId } : {}),
        permissionId,
        decision,
        via: 'web',
      });
      // Answered: the record is done here too (the workbench's answer reaches every other surface itself).
      await routes.setNotificationState(n.projectId, n.id, n.version, 'done').catch(() => undefined);
      onChange();
    } catch (e) {
      setError(liveProblem(e));
    } finally {
      setBusy(undefined);
    }
  };
  const liveAnswers = Boolean(live?.permissionId) || n.actions.some((a) => Boolean(live) && answersApproval(a) && typeof a.params?.permissionId === 'string');
  // A live approval that came without its own Approve and Refuse gets them, when it names the request.
  const extraAnswers = Boolean(live?.permissionId) && !n.actions.some(answersApproval);
  const { account } = useAuth();
  const me = n.by.principal && n.by.principal === account?.id;
  const agentName = useAgentName();
  const actor = agentName(n.by.actor);
  // A person by name, never their id: unresolved, they are someone on the project.
  const who = actor ? (me ? `${actor} for you` : actor) : me ? 'you' : n.by.principal ? <SubjectName id={n.by.principal} fallback="someone on the project" /> : null;
  // "For you" only when the record is addressed to this person; the project's news reads FYI.
  const attention = attentionFor(n, { id: account?.id });
  return (
    <div className={`flex items-start gap-3 text-sm ${settled ? 'text-muted' : ''}`} data-inbox-item={keyOf(n)} data-inbox-state={n.state}>
      {gutter && !onSelect && <span className="w-4 shrink-0" aria-hidden="true" />}
      {onSelect && (
        <span className="pt-1.5">
          <Tick checked={Boolean(selected)} onChange={onSelect} label={`Select ${n.title}`} />
        </span>
      )}
      <span className="list-row__icon" aria-hidden="true">
        {KIND_ICON[n.kind] ?? <Bell />}
      </span>
      <div className="grid min-w-0 flex-1 gap-2">
        <div className="min-w-0">
          <div className="flex min-w-0 items-start gap-2">
            <span className={`min-w-0 flex-1 ${n.state === 'unread' && !settled ? 'font-medium' : ''}`}>{n.title}</span>
            {n.state === 'unread' && !settled && <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent" role="img" aria-label="Unread" title="Unread" />}
          </div>
          {n.riskLevel !== undefined && (
            <div className="mt-1">
              <StatusMark wrap state={n.riskLevel <= 2 ? 'online' : n.riskLevel === 3 ? 'idle' : 'high'} label={`Risk level ${n.riskLevel} out of 5 · ${n.riskWords ?? RISK_LEVEL_WORDS[n.riskLevel as 1 | 2 | 3 | 4 | 5] ?? ''}${n.riskLevel >= 4 ? ' · an owner must approve this' : ''}`} />
            </div>
          )}
          {n.desktopOnly && !settled && <div className="mt-1 text-xs text-muted">{n.desktopOnly}</div>}
          {n.body && <Markdown compact breaks className="mt-0.5 text-xs text-muted" text={n.body} />}
          <div className="mt-1 text-xs text-muted">
            {ATTENTION_TEXT[attention]} · {KIND_TEXT[n.kind]}
            {showProject && n.projectName && (
              <>
                {' · '}
                <Link to={`/projects/${encodeURIComponent(n.projectId)}?tab=inbox`} className="text-accent hover:underline">
                  {n.projectName}
                </Link>
              </>
            )}
            {n.taskId && ` · ${n.taskId}`}
            {who && <> · {who}</>}
            {` · ${timeAgo(n.at)}`}
            {settled && ` · ${n.state}`}
            {showTeam && n.teamName && ` · ${n.teamName}`}
            {n.runId && (
              <>
                {' · '}
                <span className="mono" title={n.runId}>{shortRunId(n.runId)}</span>
              </>
            )}
          </div>
        </div>
        {!settled && (n.actions.length > 0 || openLive || extraAnswers) && (
          <div className="flex flex-wrap gap-2">
            {openLive && (
              <Button size="sm" variant="secondary" onPress={() => navigate(openLive)}>
                Open live
              </Button>
            )}
            {extraAnswers && (
              <>
                {!n.desktopOnly && (
                  <Button size="sm" isDisabled={Boolean(busy)} onPress={() => void answerLive('approve', 'allow')}>
                    {busy === 'approve' ? 'Approve…' : 'Approve'}
                  </Button>
                )}
                <Button size="sm" variant="danger-soft" isDisabled={Boolean(busy)} onPress={() => void answerLive('refuse', 'deny')}>
                  {busy === 'refuse' ? 'Refuse…' : 'Refuse'}
                </Button>
              </>
            )}
            {/* Only the machine's desktop may approve it (workbench 0.89.17 `desktopOnly`): its words stand in for the allow actions. */}
            {n.actions.filter((a) => !(n.desktopOnly && a.rpc === 'permissions.resolve' && !/reject|deny|refuse|dismiss/i.test(a.id))).map((a) =>
              liveAnswers && answersApproval(a) ? (
                <Button key={a.id} size="sm" variant={approvalDecision(a) === 'deny' ? 'danger-soft' : 'primary'} isDisabled={Boolean(busy)} onPress={() => void answerLive(a.id, approvalDecision(a), a.params)}>
                  {busy === a.id ? `${a.label}…` : a.label}
                </Button>
              ) : a.rpc === SUBSCRIPTION_SIGN_IN_ACTION ? (
                // A sign-in is a link to the vendor's own page; the machine finishes it.
                <SignInLink key={a.id} label={a.label} params={a.params} answer={false} />
              ) : a.rpc === OPEN_VIEW_ACTION && (a.params as { view?: string } | undefined)?.view === 'teamwork' ? (
                // Navigation, not a call: the Team Work session, at the message the record is about.
                <Button key={a.id} size="sm" variant="secondary" onPress={() => navigate(teamworkHref(n.projectId, a.params))}>
                  {a.label}
                </Button>
              ) : a.rpc === TEAMWORK_ANSWER_RPC && typeof (a.params as { value?: unknown } | undefined)?.value !== 'string' ? (
                // An answer in words: a small field under the record, sent as the person.
                <Button key={a.id} size="sm" isDisabled={Boolean(busy)} onPress={() => setAnswering(answering === a.id ? undefined : a.id)}>
                  {answering === a.id ? 'Not now' : a.label}
                </Button>
              ) : asksWhy(a) ? (
                // Reject or Hold on a workflow's approval: why, in a field under the record, then sent with the action.
                <Button key={a.id} size="sm" variant={(a.params as { decision?: string } | undefined)?.decision === 'rejected' ? 'danger-soft' : 'secondary'} isDisabled={Boolean(busy)} onPress={() => setAnswering(answering === a.id ? undefined : a.id)}>
                  {answering === a.id ? 'Not now' : `${a.label}…`}
                </Button>
              ) : a.rpc === OPEN_MY_TASKS_ACTION ? (
                // Navigation, not a call: the project's board, where the task is the person's to move.
                <Button key={a.id} size="sm" onPress={() => navigate(`/projects/${encodeURIComponent(n.projectId)}?tab=work&part=tasks`)}>
                  {a.label}
                </Button>
              ) : a.rpc === EXCEPTION_DECIDE_RPC ? (
                // How long, a note or a reason: answered in the Policies page's Exceptions, where the request is in full.
                <Button key={a.id} size="sm" variant={(a.params as { decision?: string } | undefined)?.decision === 'approve' ? 'primary' : 'secondary'} onPress={() => navigate('/policies?view=exceptions')}>
                  {a.label}…
                </Button>
              ) : a.rpc === CATALOGUE_DECIDE_RPC && (a.params as { decision?: string } | undefined)?.decision === 'decline' ? (
                // A decline carries a reason the person reads: it is written in the Customisations queue.
                <Button key={a.id} size="sm" variant="secondary" onPress={() => navigate('/customisations')}>
                  {a.label}…
                </Button>
              ) : (
                <Button key={a.id} size="sm" variant={/reject|deny|refuse/i.test(a.id) ? 'danger-soft' : n.attention === 'needs-you' ? 'primary' : 'secondary'} isDisabled={Boolean(busy)} onPress={() => void act(a.id)}>
                  {busy === a.id ? `${a.label}…` : a.label}
                </Button>
              ),
            )}
          </div>
        )}
        {!settled && answering ? (
          <div className="grid max-w-xl gap-2">
            <textarea aria-label={asksWhy(answeringAction) ? `Why: ${n.title}` : `Your answer: ${n.title}`} className="w-full resize-y rounded-lg border border-field-border bg-field-background px-3 py-1.5 text-sm outline-none focus:border-accent" rows={2} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder={asksWhy(answeringAction) ? `Why it is ${(answeringAction?.params as { decision?: string } | undefined)?.decision ?? 'held'} (the run records this)` : 'Your answer'} />
            <div>
              <Button size="sm" variant={asksWhy(answeringAction) && (answeringAction?.params as { decision?: string } | undefined)?.decision === 'rejected' ? 'danger' : 'primary'} isDisabled={!answer.trim() || Boolean(busy)} onPress={() => void sendAnswer(answering)}>
                {busy === answering ? 'Sending…' : asksWhy(answeringAction) ? answeringAction!.label : 'Send answer'}
              </Button>
            </div>
          </div>
        ) : null}
        {!settled &&
          n.actions.map((a) => {
            const waits = a.rpc === SUBSCRIPTION_SIGN_IN_ACTION ? signInWaitsFor(a.params) : undefined;
            return waits ? (
              <div key={`answer-${a.id}`} className="max-w-xl">
                <SignInAnswer id={waits.id} wants={waits.wants} />
              </div>
            ) : null;
          })}
        <ErrorNote error={error} />
      </div>
      {!settled && (n.actions.length === 0 || onlyOpens) && !extraAnswers ? (
        <RowMenu
          label={`More for ${n.title}`}
          items={[n.state === 'unread' && !busy && { id: 'read', label: 'Mark read' }, !busy && { id: 'done', label: 'Mark done' }]}
          onAction={(id) => void mark(id === 'read' ? 'read' : 'done')}
        />
      ) : null}
    </div>
  );
}

/** Where an open action on a Team Work record goes: the session, scrolled to its message. */
function teamworkHref(projectId: string, params: Record<string, unknown> | undefined): string {
  const p = (params ?? {}) as { id?: string; projectId?: string; messageId?: string };
  const project = p.projectId || projectId;
  return `/projects/${encodeURIComponent(project)}/teamwork/${encodeURIComponent(p.id ?? '')}${p.messageId ? `?message=${encodeURIComponent(p.messageId)}` : ''}`;
}

/** A project's own inbox; `needsYouOnly` is the strip on the overview. */
export function ProjectInbox({ projectId, projectName, needsYouOnly }: { projectId: string; projectName?: string; needsYouOnly?: boolean }) {
  const { account } = useAuth();
  const list = useLoad(() => routes.projectNotifications(projectId, { limit: needsYouOnly ? 50 : 200 }), [projectId], 30_000);
  const items = oneRecordPerTask(list.data?.items ?? [], account?.id).map((n) => ({ ...n, projectName })).filter((n) => !needsYouOnly || (n.attention === 'needs-you' && (n.state === 'unread' || n.state === 'read')));
  if (needsYouOnly && items.length === 0) return null;
  return (
    <InboxList
      items={items}
      onChange={list.reload}
      selectable={!needsYouOnly}
      title={needsYouOnly ? 'Needs you' : 'Inbox'}
      description={needsYouOnly ? undefined : "What happened in this project: approvals, decisions and changes, from every member's workbench and the organisation."}
      actions={needsYouOnly ? undefined : <MarkAllRead items={items} projectId={projectId} onChange={list.reload} />}
      error={list.error}
      emptyTitle="Nothing here yet"
      emptyBody="Changes to the shared board, decisions and approvals from members' workbenches show here."
    />
  );
}
